import { ReactNode, useMemo, useState } from 'react'

import { Menu, TextInput } from '@mantine/core'

import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconSearch from 'src/icons/IconSearch'

export interface CustomFieldVariable {
  name: string
  value: string
}

interface CustomFieldVariableMenuProps {
  variables: CustomFieldVariable[]
  onSelect: (value: string) => void
  renderTarget: (label: ReactNode) => ReactNode
}

const CustomFieldVariableMenu = ({
  variables,
  onSelect,
  renderTarget,
}: CustomFieldVariableMenuProps) => {
  const [opened, setOpened] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return variables
    return variables.filter((variable) =>
      variable.name.toLowerCase().includes(term)
    )
  }, [variables, search])

  if (variables.length === 0) return null

  const label = (
    <span className="inline-flex items-center gap-1">
      Custom fields ({variables.length})
      <IconChevronDown className="h-3 w-3 fill-none stroke-current" />
    </span>
  )

  return (
    <StyledMenu
      width={260}
      position="bottom-start"
      opened={opened}
      onChange={(next: boolean) => {
        setOpened(next)
        if (!next) setSearch('')
      }}
    >
      <Menu.Target>
        <button type="button" aria-label="Insert a custom field">
          {renderTarget(label)}
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <div className="flex max-h-[300px] flex-col">
          <div className="shrink-0 bg-white px-2 pb-1">
            <TextInput
              placeholder="Search custom fields..."
              size="xs"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              // Keep arrow and letter keys in the search box instead of
              // letting the menu's own keyboard navigation consume them.
              onKeyDown={(e) => e.stopPropagation()}
              leftSection={
                <IconSearch className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-400" />
              }
              data-autofocus
            />
          </div>
          <div className="min-h-0 overflow-y-auto">
            {filtered.length === 0 ? (
              <Menu.Item className="text-xs" disabled>
                No custom fields found
              </Menu.Item>
            ) : (
              filtered.map((variable) => (
                <Menu.Item
                  key={variable.value}
                  title={variable.name}
                  className="text-xs"
                  onClick={() => onSelect(variable.value)}
                >
                  {variable.name}
                </Menu.Item>
              ))
            )}
          </div>
        </div>
      </Menu.Dropdown>
    </StyledMenu>
  )
}

export default CustomFieldVariableMenu
